import React, { useState } from 'react';
import { CartItem } from '../../services/cartStorage';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onUpdateQty: (id: string | number, delta: number) => void;
  onRemove: (id: string | number) => void;
  onCheckout: () => void;
}

const ShoppingCart: React.FC<Props> = ({ isOpen, onClose, items, onUpdateQty, onRemove, onCheckout }) => {
  const [promoCode, setPromoCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{ code: string, percent: number } | null>(null);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');

  // Local state removed - strictly controlled by props now

  const handleUpdateQty = (id: string | number, delta: number) => {
    onUpdateQty(id, delta);
  };

  const handleRemove = (id: string | number) => {
    onRemove(id);
  };

  const handleCheckout = () => {
    onCheckout();
  };

  const handleApplyPromo = () => {
    setPromoError('');
    setPromoSuccess('');
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    if (code === 'SAVE20') {
      setAppliedDiscount({ code: 'SAVE20', percent: 0.20 });
      setPromoSuccess('20% discount applied!');
    } else if (code === 'PETLOVE') {
      setAppliedDiscount({ code: 'PETLOVE', percent: 0.10 });
      setPromoSuccess('10% discount applied!');
    } else {
      setPromoError('Invalid promo code');
      setAppliedDiscount(null);
    }
  };

  const removePromo = () => {
    setAppliedDiscount(null);
    setPromoCode('');
    setPromoSuccess('');
    setPromoError('');
  };

  const displayItems = items;
  const subtotal = displayItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
  const taxRate = 0.08;
  const discountAmount = appliedDiscount ? subtotal * appliedDiscount.percent : 0;
  const taxableAmount = Math.max(0, subtotal - discountAmount);
  const taxAmount = taxableAmount * taxRate;
  const total = taxableAmount + taxAmount;
  const cartCount = displayItems.reduce((acc, i) => acc + i.qty, 0);

  return (
    <>
      <div
        className={`fixed inset-0 bg-primary-dark/40 backdrop-blur-md z-[60] transition-opacity duration-500 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      <div className={`fixed inset-y-0 right-0 w-full max-w-md bg-white/90 dark:bg-surface-dark/90 backdrop-blur-2xl z-[70] shadow-2xl transform transition-all duration-500 ease-out border-l border-white/20 ${isOpen ? 'translate-x-0' : 'translate-x-full opacity-0'}`}>
        <div className="h-full flex flex-col">
          {/* Cart Header */}
          <div className="px-8 py-8 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                <span className="material-icons-round text-2xl">shopping_cart</span>
              </div>
              <div>
                <h2 className="text-xl font-black text-primary-dark dark:text-white uppercase tracking-tight">Supply Cart</h2>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{cartCount} items ready</p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-gray-50 dark:bg-background-dark/50 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all border border-gray-100 dark:border-gray-800 shadow-sm"
            >
              <span className="material-icons-round">close</span>
            </button>
          </div>

          {/* Cart Content */}
          <div className="flex-1 overflow-y-auto p-8 space-y-6 scrollbar-hide">
            {displayItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-6 py-12">
                <div className="w-32 h-32 rounded-full bg-gray-50 dark:bg-background-dark/50 flex items-center justify-center border-2 border-dashed border-gray-100 dark:border-gray-800 relative">
                  <span className="material-icons-round text-5xl text-gray-200">shopping_bag</span>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-white dark:bg-surface-dark rounded-full shadow-lg flex items-center justify-center border border-gray-100 dark:border-gray-800">
                    <span className="material-icons-round text-primary-dark dark:text-gray-400 text-lg">search</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-black text-primary-dark dark:text-white uppercase tracking-tight">Cart is offline</h3>
                  <p className="text-xs font-medium text-gray-400 max-w-[200px] mx-auto leading-relaxed">No supplies added to the shipment manifest yet.</p>
                </div>
                <button
                  onClick={onClose}
                  className="text-primary font-black uppercase text-[10px] tracking-[0.2em] hover:tracking-[0.3em] transition-all"
                >
                  Browse Shop Center
                </button>
              </div>
            ) : (
              displayItems.map(item => (
                <div key={item.id} className="animate-fade-in">
                  <CartItemRow
                    item={item}
                    onUpdateQty={handleUpdateQty}
                    onRemove={handleRemove}
                  />
                </div>
              ))
            )}
          </div>

          {/* Cart Footer */}
          <div className="p-8 bg-white dark:bg-background-dark border-t border-gray-100 dark:border-gray-800 shadow-[0_-20px_40px_-20px_rgba(0,0,0,0.05)]">

            <div className="mb-8">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1 mb-3 block text-center">Protocol Discount</label>
              {appliedDiscount ? (
                <div className="flex justify-between items-center bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-[1.4rem] px-5 py-4 group">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-primary text-white flex items-center justify-center shadow-lg shadow-primary/20">
                      <span className="material-icons-round text-sm">confirmation_number</span>
                    </div>
                    <div>
                      <span className="text-xs font-black text-primary uppercase tracking-wider">{appliedDiscount.code} Node</span>
                      <p className="text-[9px] font-bold text-primary/60 uppercase">Protocol Accepted</p>
                    </div>
                  </div>
                  <button onClick={removePromo} className="w-8 h-8 rounded-lg bg-white/50 dark:bg-black/50 text-gray-400 hover:text-red-500 transition-all">
                    <span className="material-icons-round text-sm">close</span>
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                    placeholder="AUTH-CODE-20"
                    className="flex-1 bg-gray-50 dark:bg-surface-dark/50 border border-gray-100 dark:border-gray-800 rounded-2xl px-5 py-4 text-xs font-black focus:ring-4 focus:ring-primary/10 focus:border-primary focus:outline-none transition-all placeholder:text-gray-300"
                  />
                  <button
                    onClick={handleApplyPromo}
                    disabled={!promoCode}
                    className="bg-primary-dark dark:bg-surface-dark text-white px-6 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black dark:hover:bg-primary transition-all disabled:opacity-30 shadow-xl shadow-black/5"
                  >
                    Verify
                  </button>
                </div>
              )}
              {promoError && <p className="text-[10px] text-red-500 mt-2 font-black uppercase tracking-tighter text-center">{promoError}</p>}
            </div>

            <div className="space-y-3 mb-8">
              <div className="flex justify-between text-[11px] font-black text-gray-400 uppercase tracking-widest">
                <span>Manifest Subtotal</span>
                <span className="text-primary-dark dark:text-gray-200">${subtotal.toFixed(2)}</span>
              </div>
              {appliedDiscount && (
                <div className="flex justify-between text-[11px] font-black text-primary uppercase tracking-widest">
                  <span>Discount Algorithm</span>
                  <span>-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-[11px] font-black text-gray-400 uppercase tracking-widest">
                <span>Service Tax (8%)</span>
                <span className="text-primary-dark dark:text-gray-200">${taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-end pt-5 border-t border-gray-100 dark:border-gray-800 mt-2">
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-1">Final Clearance</span>
                  <span className="text-3xl font-black text-primary-dark dark:text-white tracking-tighter leading-none">${total.toFixed(2)}</span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-[9px] font-black text-gray-300 uppercase tracking-[0.1em]">Secured Connection</span>
                  <span className="material-icons-round text-gray-200 text-lg">verified_user</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              disabled={displayItems.length === 0}
              className="group w-full bg-primary hover:bg-primary-dark text-white font-black py-5 rounded-[1.8rem] shadow-2xl shadow-primary/30 transition-all flex items-center justify-center gap-3 disabled:opacity-30 disabled:scale-100 transform active:scale-95 uppercase text-xs tracking-[0.15em]"
            >
              Initialize Checkout
              <span className="material-icons-round text-lg group-hover:translate-x-1 transition-transform">rocket_launch</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const CartItemRow = ({ item, onUpdateQty, onRemove }: { item: CartItem, onUpdateQty: (id: string | number, delta: number) => void, onRemove: (id: string | number) => void }) => (
  <div className="flex gap-5 group p-5 rounded-[2rem] border border-transparent hover:border-gray-100 dark:hover:border-gray-800 hover:bg-white dark:hover:bg-surface-dark transition-all duration-300 hover:shadow-xl hover:shadow-black/5 relative">
    <div className="w-24 h-24 rounded-[1.4rem] bg-gray-50 dark:bg-background-dark/50 overflow-hidden shrink-0 border border-gray-100 dark:border-gray-800 shadow-inner group-hover:scale-105 transition-transform duration-500">
      <img src={item.image} alt={item.name} className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all" />
    </div>
    <div className="flex-1 flex flex-col justify-between py-1">
      <div>
        <div className="flex justify-between items-start mb-1">
          <h3 className="font-black text-sm text-primary-dark dark:text-white leading-tight pr-4 line-clamp-2">{item.name}</h3>
          <button
            onClick={() => onRemove(item.id)}
            className="w-8 h-8 rounded-full bg-white dark:bg-background-dark border border-gray-100 dark:border-gray-800 flex items-center justify-center text-gray-300 hover:text-red-500 hover:border-red-100 dark:hover:border-red-900/40 transition-all opacity-0 group-hover:opacity-100 shadow-sm"
          >
            <span className="material-icons-round text-sm">close</span>
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black text-primary uppercase tracking-widest">{item.category || 'Supply'}</span>
          <span className="text-[10px] font-black text-primary-dark dark:text-white">${item.price.toFixed(2)}</span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 bg-gray-50 dark:bg-background-dark/50 rounded-xl px-2 py-1 border border-gray-100 dark:border-gray-800">
          <button
            onClick={() => onUpdateQty(item.id, -1)}
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-primary transition-colors text-lg"
          >
            <span className="material-icons-round text-sm">remove</span>
          </button>
          <span className="text-xs font-black text-primary-dark dark:text-white min-w-[12px] text-center">{item.qty}</span>
          <button
            onClick={() => onUpdateQty(item.id, 1)}
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-primary transition-colors text-lg"
          >
            <span className="material-icons-round text-sm">add</span>
          </button>
        </div>
        <div className="text-right">
          <span className="text-[10px] font-black text-gray-300 uppercase tracking-tighter block leading-none mb-1">Row Total</span>
          <span className="text-sm font-black text-primary-dark dark:text-white">${(item.price * item.qty).toFixed(2)}</span>
        </div>
      </div>
    </div>
  </div>
);

export default ShoppingCart;
